var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * CZNA-510 — the ontology-to-form map, drawn.
 *
 * <p>Three bands left to right: the class, the properties its `domain` admits, and
 * the MARC 21 fields those properties project onto. Edges are the ontology's `binds`.
 * The fields are coloured by whether a form of this class's authority types offers
 * them, which is the half the tables have never shown.
 *
 * <p><b>Hand-written SVG, no library.</b> The module carries no graph or chart
 * dependency and the one existing chart (`Quality/QualityTrendChart`) is inline SVG
 * too. A layered two-column graph is arithmetic — the layout here is `index * PITCH`
 * — and it is not worth a bundle, a licence and a review to have someone else do it.
 *
 * <p><b>The nodes are buttons, and that is not decoration.</b> Every node carries
 * `role="button"` and an `aria-label`, so the map lands in the accessibility tree and
 * is therefore readable by `ui-probe` as text — which is how it gets verified at all
 * without shipping screenshots into anyone's context. It also keeps the map from
 * being a dead end: a click opens the existing property or field detail.
 *
 * <p><b>Hovering dims the rest.</b> The worst class (Osoba) draws 45 edges between 30
 * and 33 nodes; every edge visible at once is a picture nobody reads twice, which is
 * the failure the ticket named. Focus is hover- AND focus-driven so the keyboard path
 * gets it too.
 */
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { fieldFormStatusColor, } from './ontologyFormMap';
/** Geometry. One pitch per row; the taller column decides the height. */
var PITCH = 30;
var NODE_H = 24;
var CLASS_W = 150;
var PROP_W = 250;
var FIELD_W = 270;
var GAP = 70;
var PAD_TOP = 28;
var PAD_BOTTOM = 12;
/** Both header lines must fit: the second baseline sits at PAD_TOP + 38. */
var CLASS_MIN_H = 46;
var PROP_X = CLASS_W + GAP;
var FIELD_X = PROP_X + PROP_W + GAP;
var WIDTH = FIELD_X + FIELD_W;
var rowY = function (index) { return PAD_TOP + index * PITCH; };
var midY = function (index) { return rowY(index) + NODE_H / 2; };
var GROUP_COLOR = {
    entity: '#2c5cc5',
    record: '#5a6b7d',
    vocabulary: '#7a4fa3',
};
/** Truncate to fit the box — SVG text does not wrap and will happily overflow. */
var clip = function (text, max) { return (text.length <= max ? text : "".concat(text.slice(0, max - 1), "\u2026")); };
var OntologyMapDiagram = function (_a) {
    var _b, _c;
    var map = _a.map, onOpenProperty = _a.onOpenProperty, onOpenField = _a.onOpenField;
    var intl = useIntl();
    /** `property:NAME` or `field:TAG`; null = nothing focused, everything at full strength. */
    var _d = useState(null), focus = _d[0], setFocus = _d[1];
    var t = function (id, values) { return intl.formatMessage({ id: "ui-czna-authorities.semanticCatalog.map.".concat(id) }, values); };
    var properties = map.properties, fields = map.fields, edges = map.edges;
    var propertyIndex = new Map(properties.map(function (p, i) { return [p.name, i]; }));
    var fieldIndex = new Map(fields.map(function (f, i) { return [f.tag, i]; }));
    var rows = Math.max(properties.length, fields.length);
    var height = PAD_TOP + Math.max(rows, 1) * PITCH + PAD_BOTTOM;
    /** Which nodes/edges the current focus keeps lit. Empty set = no focus, all lit. */
    var litProperties = new Set();
    var litFields = new Set();
    if (focus) {
        var _e = [focus.slice(0, focus.indexOf(':')), focus.slice(focus.indexOf(':') + 1)], kind_1 = _e[0], key_1 = _e[1];
        edges.forEach(function (edge) {
            if (kind_1 === 'property' && edge.property === key_1) {
                litProperties.add(edge.property);
                litFields.add(edge.tag);
            }
            if (kind_1 === 'field' && edge.tag === key_1) {
                litProperties.add(edge.property);
                litFields.add(edge.tag);
            }
        });
        // A node with no edge still lights itself, or focusing it would blank the picture.
        if (kind_1 === 'property')
            litProperties.add(key_1);
        if (kind_1 === 'field')
            litFields.add(key_1);
    }
    var dimmed = function (lit) { return (!focus || lit ? 1 : 0.18); };
    var statusLabel = function (node) { return t("status.".concat(node.status)); };
    var nodeProps = function (id, label, onOpen) { return ({
        role: 'button',
        tabIndex: 0,
        'aria-label': label,
        'data-testid': id,
        style: { cursor: 'pointer' },
        onClick: onOpen,
        onKeyDown: function (event) {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen();
            }
        },
    }); };
    var renderProperty = function (node, index) {
        var lit = litProperties.has(node.name);
        var label = t('aria.property', {
            label: node.label, name: node.name, tags: node.tags.length,
        });
        return (React.createElement("g", __assign({ key: node.name, opacity: dimmed(lit), onMouseEnter: function () { return setFocus("property:".concat(node.name)); }, onMouseLeave: function () { return setFocus(null); }, onFocus: function () { return setFocus("property:".concat(node.name)); }, onBlur: function () { return setFocus(null); } }, nodeProps("ontology-map-property-".concat(node.name), label, function () { return onOpenProperty(node.name); })),
            React.createElement("title", null, label),
            React.createElement("rect", { x: PROP_X, y: rowY(index), width: PROP_W, height: NODE_H, rx: 3, fill: "#f4f7fa", stroke: node.tags.length === 0 ? '#b06000' : '#c3cfdb', strokeDasharray: node.tags.length === 0 ? '3 2' : undefined }),
            React.createElement("text", { x: PROP_X + 8, y: midY(index) + 4, fontSize: "11", fill: "#26343d" }, clip(node.label, 34))));
    };
    var renderField = function (node, index) {
        var lit = litFields.has(node.tag);
        var color = fieldFormStatusColor(node.status);
        /**
         * `offeredBy` belongs in the label, not only in the model. On a class with several
         * types (`Concept` has two, `AdminMetadata` every one) green means "at least one
         * type offers it", and without the list the reader cannot tell all-of-them from
         * one-of-them. Absent for the non-green states, where there is nothing to name.
         */
        var label = node.offeredBy.length > 0
            ? t('aria.fieldOfferedBy', {
                tag: node.tag,
                label: node.label,
                status: statusLabel(node),
                types: node.offeredBy.join(', '),
            })
            : t('aria.field', { tag: node.tag, label: node.label, status: statusLabel(node) });
        return (React.createElement("g", __assign({ key: node.tag, opacity: dimmed(lit), onMouseEnter: function () { return setFocus("field:".concat(node.tag)); }, onMouseLeave: function () { return setFocus(null); }, onFocus: function () { return setFocus("field:".concat(node.tag)); }, onBlur: function () { return setFocus(null); } }, nodeProps("ontology-map-field-".concat(node.tag), label, function () { return onOpenField(node.tag); })),
            React.createElement("title", null, label),
            React.createElement("rect", { x: FIELD_X, y: rowY(index), width: FIELD_W, height: NODE_H, rx: 3, fill: "#fff", stroke: "#c3cfdb" }),
            React.createElement("rect", { x: FIELD_X, y: rowY(index), width: 5, height: NODE_H, rx: 2, fill: color }),
            React.createElement("text", { x: FIELD_X + 14, y: midY(index) + 4, fontSize: "11", fill: "#26343d" },
                React.createElement("tspan", { fontWeight: "700" }, node.tag),
                React.createElement("tspan", { dx: "6" }, clip(node.label, 28)))));
    };
    var renderEdge = function (edge) {
        var from = propertyIndex.get(edge.property);
        var to = fieldIndex.get(edge.tag);
        if (from === undefined || to === undefined)
            return null;
        var lit = litProperties.has(edge.property) && litFields.has(edge.tag);
        var x1 = PROP_X + PROP_W;
        var x2 = FIELD_X;
        var y1 = midY(from);
        var y2 = midY(to);
        var bend = GAP / 2;
        return (React.createElement("path", { key: "".concat(edge.property, "-").concat(edge.tag), d: "M ".concat(x1, " ").concat(y1, " C ").concat(x1 + bend, " ").concat(y1, ", ").concat(x2 - bend, " ").concat(y2, ", ").concat(x2, " ").concat(y2), fill: "none", stroke: focus && lit ? '#2c5cc5' : '#9fb2c4', strokeWidth: focus && lit ? 1.6 : 1, opacity: dimmed(lit) }));
    };
    if (properties.length === 0 && fields.length === 0) {
        return (React.createElement("p", { "data-testid": "ontology-map-empty" }, t('emptyClass', { class: map.classLabel })));
    }
    return (React.createElement("div", { style: { overflowX: 'auto' }, "data-testid": "ontology-map-diagram" },
        React.createElement("svg", { viewBox: "0 0 ".concat(WIDTH, " ").concat(height), width: WIDTH, height: height, 
            // `minWidth` and NOT `maxWidth: 100%`: with the latter the fixed `height`
            // attribute made a narrow pane scale the whole box down to fit the width
            // (11px text at ~8px, blank bands above and below) and the `overflow-x`
            // wrapper never engaged. Person's box is 810x940, so that was the common case.
            style: { minWidth: WIDTH, display: 'block' }, "aria-label": t('aria.diagram', {
                class: map.classLabel,
                properties: properties.length,
                fields: fields.length,
            }) },
            React.createElement("text", { x: PROP_X, y: 16, fontSize: "11", fontWeight: "700", fill: "#8a97a6" }, t('column.properties', { count: properties.length })),
            React.createElement("text", { x: FIELD_X, y: 16, fontSize: "11", fontWeight: "700", fill: "#8a97a6" }, t('column.fields', { count: fields.length })),
            edges.map(renderEdge),
            React.createElement("g", { "data-testid": "ontology-map-class" },
                React.createElement("rect", { x: 0, y: PAD_TOP, width: CLASS_W, height: Math.max(Math.max(rows, 1) * PITCH - (PITCH - NODE_H), CLASS_MIN_H), rx: 4, fill: (_c = GROUP_COLOR[(_b = map.group) !== null && _b !== void 0 ? _b : '']) !== null && _c !== void 0 ? _c : GROUP_COLOR.entity }),
                React.createElement("text", { x: CLASS_W / 2, y: PAD_TOP + 20, fontSize: "13", fontWeight: "700", fill: "#fff", textAnchor: "middle" }, clip(map.classLabel, 18)),
                React.createElement("text", { x: CLASS_W / 2, y: PAD_TOP + 38, fontSize: "10", fill: "#e8eef7", textAnchor: "middle" }, map.className)),
            properties.map(renderProperty),
            fields.map(renderField))));
};
export default OntologyMapDiagram;
